'use client';

import React from 'react';
import type { Character } from '../types';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Badge } from '@/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Pencil } from 'lucide-react';

/**
 * Shared character row — used by the wizard CharacterSelector AND the book
 * hub picker (CharacterPickerSheet) so both read identically. Selection is
 * a checkbox (multi in the wizard, single-highlight in the book hub via the
 * same onToggle contract). `readOnly` hides the edit affordance (story chars).
 */
export const CharacterListItem = React.memo(function CharacterListItem({
  character,
  isSelected,
  isAtLimit,
  onToggle,
  onEdit,
  t,
  readOnly = false,
}: {
  character: Character;
  isSelected: boolean;
  isAtLimit: boolean;
  onToggle: (id: string, checked: boolean) => void;
  onEdit?: (character: Character) => void;
  // next-intl Translator — typed as any to accept any message namespace.
  t: any;
  readOnly?: boolean;
}) {
  const disabled = isAtLimit && !isSelected;
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-3 transition-colors cursor-pointer ${
        isSelected ? 'border-primary bg-accent/50' : ''
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent'}`}
      onClick={() => { if (!disabled) onToggle(character.id, !isSelected); }}
      data-testid={`character-item-${character.id}`}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          id={`character-${character.id}`}
          checked={isSelected}
          disabled={disabled}
          onCheckedChange={(checked) => onToggle(character.id, checked as boolean)}
        />
      </div>
      <Avatar className="h-10 w-10 shrink-0">
        {(character.primaryPortraitUrl || character.primaryImageUrl) && (
          <AvatarImage
            src={mediaSrc(character.primaryPortraitUrl || character.primaryImageUrl)}
            alt={character.name}
          />
        )}
        <AvatarFallback>{character.name.charAt(0).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className="cursor-pointer font-medium">
            {character.name}
          </div>
          {!readOnly && onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={(e) => { e.stopPropagation(); onEdit(character); }}
              data-testid={`character-edit-${character.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {character.usageCount > 0 && (
            <Badge variant="secondary" className="text-xs" title={t('usedInStories', { count: character.usageCount })}>
              {t('usedCount', { count: character.usageCount })}
            </Badge>
          )}
        </div>
        {character.physicalDescription && (
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
            {character.physicalDescription}
          </p>
        )}
      </div>
    </div>
  );
});
